import React, { useEffect, useState } from 'react';

/**
 * A help collection's icon, drawn the way the apps draw it.
 *
 * Collections store an Iconsax name. The apps carry every Bold glyph as path
 * data (from the same generator, `scripts/iconsax-glyphs.mjs`), so anything
 * picked here is something they can draw — on a release that has the data.
 * An older install falls back to `Book1`, and so does this, for a name that
 * is not in the set.
 *
 * The glyph data is ~600 KB, so it is its own chunk, fetched the first time
 * an icon is drawn rather than with the dashboard.
 */
type GlyphModule = typeof import('./iconsaxGlyphs');

export const FALLBACK_ICON = 'Book1';

type GlyphPath = GlyphModule['ICONSAX_GLYPHS'][string][number];

/**
 * Names the apps draw that are not Iconsax names.
 *
 * `ArrowUpRight` was lifted from the Pencil file before the apps had the
 * full set, and collections already use it. The apps still draw their own
 * version; this is the same arrow, close enough to pick it by.
 */
const APP_GLYPHS: Record<string, GlyphPath[]> = {
  ArrowUpRight: [['M7 17 17 7M8.5 7H17v8.5', 1, 1.5, 0]],
};

/** The seven icons the help centre had before the full set — the defaults. */
export const ORIGINAL_ICONS = [
  'ArrowUpRight',
  'Bank',
  'Book1',
  'Card',
  'DocumentText',
  'Profile2User',
  'ShieldTick',
];

const own = (record: object, key: string) => Object.prototype.hasOwnProperty.call(record, key);

export function glyphFor(glyphs: GlyphModule, name: string): GlyphPath[] | undefined {
  // Own keys only: a saved icon of "toString" must not find a function on
  // Object.prototype and try to draw it.
  if (own(glyphs.ICONSAX_GLYPHS, name)) return glyphs.ICONSAX_GLYPHS[name];
  if (own(APP_GLYPHS, name)) return APP_GLYPHS[name];
  return undefined;
}

let loaded: GlyphModule | null = null;
let loading: Promise<GlyphModule> | null = null;

export function loadGlyphs(): Promise<GlyphModule> {
  if (!loading) {
    loading = import('./iconsaxGlyphs').then((mod) => {
      loaded = mod;
      return mod;
    });
  }
  return loading;
}

export function useGlyphs(): GlyphModule | null {
  const [glyphs, setGlyphs] = useState(loaded);
  useEffect(() => {
    if (glyphs) return;
    let live = true;
    void loadGlyphs().then((mod) => {
      if (live) setGlyphs(mod);
    });
    return () => {
      live = false;
    };
  }, [glyphs]);
  return glyphs;
}

export const HelpIcon: React.FC<{ name: string; size?: number; color?: string }> = ({
  name,
  size = 20,
  color = 'currentColor',
}) => {
  const glyphs = useGlyphs();
  const paths = glyphs ? glyphFor(glyphs, name) ?? glyphFor(glyphs, FALLBACK_ICON) ?? [] : [];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths.map((path, i) =>
        typeof path === 'string' ? (
          <path key={i} d={path} fill={color} />
        ) : (
          <path
            key={i}
            d={path[0]}
            opacity={path[1]}
            fill={path[3] ? color : 'none'}
            stroke={path[2] ? color : undefined}
            strokeWidth={path[2] || undefined}
            strokeMiterlimit={10}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ),
      )}
    </svg>
  );
};

/**
 * The accent roles, in dashboard colours.
 *
 * The apps resolve these against their own themes; this is only close enough
 * to tell "violet" from "warning" while choosing.
 */
export const ACCENT_STYLE: Record<string, { fill: string; color: string }> = {
  info: { fill: 'var(--blue-100)', color: 'var(--blue-700)' },
  positive: { fill: 'var(--green-100)', color: 'var(--green-800)' },
  warning: { fill: 'var(--warning-100)', color: 'var(--warning-900)' },
  danger: { fill: 'var(--danger-100)', color: 'var(--danger-600)' },
  violet: { fill: 'var(--purple-50)', color: 'var(--purple-600)' },
  neutral: { fill: 'var(--neutral-200)', color: 'var(--neutral-700)' },
};

/** The icon on its accent tile, as a collection appears in the app. */
export const HelpTile: React.FC<{ icon: string; accent: string; size?: number }> = ({
  icon,
  accent,
  size = 32,
}) => {
  const style = ACCENT_STYLE[accent] ?? ACCENT_STYLE.neutral;
  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: Math.round(size * 0.3),
        background: style.fill,
      }}
    >
      <HelpIcon name={icon} size={Math.round(size * 0.55)} color={style.color} />
    </span>
  );
};
